import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faIdCard, faCheckCircle, faLock, faMotorcycle, faCar } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../USER/Auth/AuthContext';
import { upsertDriverProfile, getDriverProfile } from '../services/deliveryServices';
import Swal from 'sweetalert2';
import { VEHICLE_TYPES, VEHICLE_TYPE_LABELS, DRIVER_STATUSES, ROLES } from '../../../constants';
import { getRoleIdByName, assignRoleToUser, setUserProfileRole } from '../../../services/api/trips-ui';

function DriverRegister() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    vehicle_type: VEHICLE_TYPES.MOTO,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [driverStatus, setDriverStatus] = useState(null);

  useEffect(() => {
    async function loadProfile() {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      try {
        const profile = await getDriverProfile(currentUser.id);
        if (profile) {
          setFormData({
            name: profile.name || '',
            phone: profile.phone || '',
            vehicle_type: profile.vehicle_type || VEHICLE_TYPES.MOTO,
          });
          setRegistered(true);
          setDriverStatus(profile.status);
        }
      } catch (error) {
        console.error("Error loading driver profile:", error);
      } finally {
        setLoading(false);
      }
    }
    loadProfile();
  }, [currentUser]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || registered) return;

    setSaving(true);
    try {
      await upsertDriverProfile({
        driver_uid: currentUser.id,
        ...formData,
        status: DRIVER_STATUSES.PENDING,
        rating: 5.0,
        total_trips: 0,
      });

      // ISS-01: Assign driver role (inactive until admin approves) so AuthContext.isDriver = true
      const { data: driverRole, error: roleError } = await getRoleIdByName(ROLES.DRIVER);
      if (roleError) throw roleError;

      if (driverRole?.id) {
        // Upsert driver role as inactive (pending admin activation)
        await assignRoleToUser(currentUser.id, driverRole.id, currentUser.id);
      }

      // Sync profiles.role so AuthContext picks up the change
      await setUserProfileRole(currentUser.id, ROLES.DRIVER);

      setRegistered(true);
      setDriverStatus(DRIVER_STATUSES.PENDING);
      Swal.fire({
        icon: 'success',
        title: '¡Solicitud Enviada!',
        text: 'Tu perfil ha sido registrado. Ahora agrega tus vehículos desde tu perfil.',
        confirmButtonColor: '#00C49F',
      });
    } catch (error) {
      console.error('Error al registrar conductor:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo enviar tu solicitud. Intenta de nuevo.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center mt-5"><Spinner animation="border" variant="success" /></div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="container mt-4"
    >
      <Card className="shadow-sm" style={{ borderRadius: '16px' }}>
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <FontAwesomeIcon icon={faIdCard} size="3x" className="text-success mb-2" />
            <h3>Registro de Conductor</h3>
            <p className="text-muted">Únete a la flota de Bryemma en El Tigre</p>
          </div>

          {registered && driverStatus === DRIVER_STATUSES.PENDING && (
            <Alert variant="warning" className="text-center border-0 shadow-sm rounded-16 p-4">
              <span style={{ fontSize: '3rem', display: 'block', marginBottom: '16px' }}>⏳</span>
              <h5 className="fw-bold text-dark">Registro en espera de activación</h5>
              <p className="mb-2">Tu perfil ha sido guardado de forma inmutable y está <strong>pendiente de aprobación</strong>.</p>
              <p className="text-primary fw-bold m-0" style={{ fontSize: '1.1rem' }}>
                ¡Tienes 1 día hábil para formalizar tu registro!
              </p>
              <p className="text-muted mt-2 small">
                Debes dirigirte a nuestra oficina más cercana para consignar tus documentos (Cédula, Licencia, Título y Seguro) y validar tu activación como conductor.
              </p>
            </Alert>
          )}

          {registered && driverStatus === DRIVER_STATUSES.ACTIVE && (
            <Alert variant="success" className="text-center border-0 shadow-sm rounded-16 p-4">
              <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
              <h5 className="fw-bold">¡Perfil Verificado!</h5>
              <p className="m-0 text-muted">Tus datos como conductor están verificados y asegurados.</p>
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faIdCard} className="me-1" /> Nombre completo
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Tu nombre completo"
                    required
                    disabled={registered}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faPhone} className="me-1" /> Teléfono
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="0412-1234567"
                    required
                    disabled={registered}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-1">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    <FontAwesomeIcon icon={formData.vehicle_type === VEHICLE_TYPES.CARRO ? faCar : faMotorcycle} className="me-1" />
                    Tipo de vehículo
                  </Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="vehicle-moto"
                      name="vehicle_type"
                      value={VEHICLE_TYPES.MOTO}
                      label={
                        <span>
                          <FontAwesomeIcon icon={faMotorcycle} className="me-1 text-success" />
                          {VEHICLE_TYPE_LABELS[VEHICLE_TYPES.MOTO]}
                        </span>
                      }
                      checked={formData.vehicle_type === VEHICLE_TYPES.MOTO}
                      onChange={handleChange}
                      disabled={registered}
                      inline
                      className="fw-bold"
                    />
                    <Form.Check
                      type="radio"
                      id="vehicle-carro"
                      name="vehicle_type"
                      value={VEHICLE_TYPES.CARRO}
                      label={
                        <span>
                          <FontAwesomeIcon icon={faCar} className="me-1 text-primary" />
                          {VEHICLE_TYPE_LABELS[VEHICLE_TYPES.CARRO]}
                        </span>
                      }
                      checked={formData.vehicle_type === VEHICLE_TYPES.CARRO}
                      onChange={handleChange}
                      disabled={registered}
                      inline
                      className="fw-bold"
                    />
                  </div>
                  <Form.Text className="text-muted">
                    Los usuarios podrán identificar y solicitar tu servicio según el tipo de vehículo
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {!registered && (
              <Button
                type="submit"
                variant="success"
                className="w-100 mt-4 rounded-pill"
                size="lg"
                disabled={saving}
              >
                {saving ? 'Enviando...' : '🚀 Guardar y Continuar'}
              </Button>
            )}
            
            {registered && (
              <div className="mt-4 text-center">
                <p className="text-muted small mb-3">
                  <FontAwesomeIcon icon={faLock} className="me-1" /> Tus datos iniciales de registro están guardados.
                </p>
                <div className="d-flex justify-content-center gap-2">
                  <Button
                    variant="outline-secondary"
                    className="rounded-pill px-4"
                    onClick={() => navigate('/perfil')}
                  >
                    Volver a Mi Perfil
                  </Button>
                  <Button
                    variant="success"
                    className="rounded-pill px-4"
                    onClick={() => navigate('/perfil#vehiculos')}
                  >
                    Ver / Gestionar Vehículos
                  </Button>
                </div>
              </div>
            )}
          </Form>
        </Card.Body>
      </Card>
    </motion.div>
  );
}

export default DriverRegister;
